import { useCallback } from "react";
import { Clock, Search, CheckCircle2, Loader2, BadgeCheck, XCircle, Ban, AlertTriangle, Landmark, Wallet, Smartphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtMoney } from "@/lib/payments";

// Withdrawal lifecycle (mirrors backend/wallet_core.py TRANSITIONS).
export const W_STATUS = {
  requested: { key: "wallet.status_requested", fr: "Demandé", bg: "#FEF3C7", color: "#92400E", icon: Clock },
  under_review: { key: "wallet.status_under_review", fr: "En examen", bg: "#FEF3C7", color: "#92400E", icon: Search },
  approved: { key: "wallet.status_approved", fr: "Approuvé", bg: "#DBEAFE", color: "#1E40AF", icon: CheckCircle2 },
  processing: { key: "wallet.status_processing", fr: "Versement en cours", bg: "#E0E7FF", color: "#3730A3", icon: Loader2 },
  paid: { key: "wallet.status_paid", fr: "Payé", bg: "#D1FAE5", color: "#065F46", icon: BadgeCheck },
  rejected: { key: "wallet.status_rejected", fr: "Rejeté", bg: "#FEE2E2", color: "#991B1B", icon: XCircle },
  cancelled: { key: "wallet.status_cancelled", fr: "Annulé", bg: "#F3F4F6", color: "#374151", icon: Ban },
  failed: { key: "wallet.status_failed", fr: "Échoué", bg: "#FEE2E2", color: "#991B1B", icon: AlertTriangle },
};
W_STATUS.pending = W_STATUS.requested; // legacy documents
export const ALL_STATUSES = ["requested", "under_review", "approved", "processing", "paid", "rejected", "cancelled", "failed"];
export const OPEN_STATUSES = ["requested", "under_review", "approved", "processing"];
export const CANCELLABLE = ["requested", "under_review"];

export const PAYOUT_TYPES = [
  { value: "bank", key: "wallet.type_bank", fr: "Virement bancaire", icon: Landmark },
  { value: "paypal", key: "wallet.type_paypal", fr: "PayPal", icon: Wallet },
  { value: "mobile_money", key: "wallet.type_mobile_money", fr: "Mobile Money", icon: Smartphone },
];
export const payoutType = (v) => PAYOUT_TYPES.find((p) => p.value === v) || { value: v, key: "", fr: v || "—", icon: Wallet };

export const KIND_META = {
  donation_credit: { key: "wallet.kind_donation_credit", fr: "Soutien reçu" },
  donation_refund: { key: "wallet.kind_donation_refund", fr: "Soutien remboursé" },
  withdrawal_hold: { key: "wallet.kind_withdrawal_hold", fr: "Retrait demandé (montant bloqué)" },
  withdrawal_release: { key: "wallet.kind_withdrawal_release", fr: "Montant recrédité" },
  withdrawal_paid: { key: "wallet.kind_withdrawal_paid", fr: "Retrait versé" },
  admin_adjust: { key: "wallet.kind_admin_adjust", fr: "Ajustement Yalola" },
  legacy_adjust: { key: "wallet.kind_legacy_adjust", fr: "Régularisation historique" },
};

export const fillVars = (s, vars = {}) => String(s ?? "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : `{${k}}`));

// One-line human summary of a (masked) payout method / snapshot.
export const maskedLine = (m) => {
  const d = m?.masked || {};
  if (m?.legacy || d.raw) return d.raw || m?.label || "";
  if (m?.type === "bank") return [d.holder_name, d.iban ? `IBAN ${d.iban}` : "", d.bank_name].filter(Boolean).join(" · ");
  if (m?.type === "paypal") return [d.email, d.holder_name].filter(Boolean).join(" · ");
  if (m?.type === "mobile_money") return [d.operator, d.phone, d.holder_name].filter(Boolean).join(" · ");
  return m?.label || "";
};

const LOCALE = { fr: "fr-FR", en: "en-GB", es: "es-ES", pt: "pt-PT" };
export const fmtDate = (v, lang = "fr", withTime = false) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(LOCALE[lang] || "fr-FR", withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" });
};

// tw(key, frFallback, vars) + money/date formatters bound to the current UI language.
export const useWalletT = (currency = "EUR") => {
  const { t, lang } = useApp();
  const tw = useCallback((key, fr, vars) => fillVars(t(key, fr), vars), [t]);
  const money = useCallback((n, cur) => fmtMoney(n, cur || currency, lang), [currency, lang]);
  const date = useCallback((v, withTime) => fmtDate(v, lang, withTime), [lang]);
  return { tw, money, date, lang };
};
